// ImagePreviewer.tsx
import React from 'react';
import { PhotoSlider } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import ReactDOM from 'react-dom';
import { OverlayRenderProps } from 'react-photo-view/dist/types';
import {
  CloseOutlined,
  RotateRightOutlined,
} from '@ant-design/icons';
import HwzComponent from '../../../common/component/HwzComponent';
import { Space } from 'antd';
import './ImagePreviewer.less';
import Matter from '../../../common/model/matter/Matter';
import Moon from '../../../common/model/global/Moon';

// 水印配置接口
interface WatermarkConfig {
  text: string;
  fontSize?: number;
  color?: string;
  opacity?: number;
  rotation?: number;
  position?: string;
}

interface ImageInfo {
  src: string;
  key: string;
  watermark?: WatermarkConfig;
}

interface IState {
  visible: boolean;
  images: ImageInfo[];
  index: number;
}

export default class ImagePreviewer extends HwzComponent<any, IState> {
  static initialized: boolean = false;
  static singletonRef = React.createRef<ImagePreviewer>();

  constructor(props: any) {
    super(props);
    this.state = {
      visible: false,
      images: [],
      index: 0
    };
  }

  // 初始化
  private static init() {
    let div: HTMLElement = document.createElement('div');
    div.id = 'photo-container';
    document.body.appendChild(div);

    ReactDOM.render(
      <ImagePreviewer ref={ImagePreviewer.singletonRef} />,
      document.getElementById('photo-container')
    );
  }

  // 展示一张图片
  static showSinglePhoto(url: string, watermark?: WatermarkConfig) {
    ImagePreviewer.showMultiPhoto([url], 0, watermark ? [watermark] : []);
  }

  // 展示一系列图片
  static showMultiPhoto(urls: string[], index: number = 0, watermarks: WatermarkConfig[] = []) {
    if (!ImagePreviewer.initialized) {
      ImagePreviewer.initialized = true;
      ImagePreviewer.init();
    }

    ImagePreviewer.singletonRef.current?.show(urls, index, watermarks);
  }

  /**
   * 自动获取水印配置并预览图片
   * 这个方法专门用于从 List.tsx 调用，自动处理水印逻辑
   */
  static async showMultiPhotoWithAutoWatermark(urls: string[], index: number = 0, matter?: Matter) {
    if (!ImagePreviewer.initialized) {
      ImagePreviewer.initialized = true;
      ImagePreviewer.init();
    }

    let watermarks: WatermarkConfig[] = [];
    
    // 如果有 matter 对象，自动获取水印配置
    if (matter) {
      const watermarkConfig = await ImagePreviewer.getWatermarkConfigForPreview(matter);
      if (watermarkConfig) {
        watermarks = [watermarkConfig];
      }
    }

    ImagePreviewer.singletonRef.current?.show(urls, index, watermarks);
  }

  /**
   * 从服务器获取水印配置
   */
  private static async fetchWatermarkConfigFromServer(): Promise<any> {
    try {
      const response = await fetch('/api/preference/watermark-config');
      
      if (response.ok) {
        const config = await response.json();
        console.log('服务器返回的水印配置:', config);
        
        if (config && typeof config === 'object') {
          // 确保 enabled 字段是布尔值
          if (typeof config.enabled === 'string') {
            config.enabled = config.enabled === 'true';
          }
          return config;
        }
      }
      return { enabled: false };
    } catch (error) {
      console.warn('获取水印配置失败:', error);
      return { enabled: false };
    }
  }

  /**
 * 获取水印配置 - 直接调用 fetchWatermarkConfigFromServer
 */
private static async getWatermarkConfigForPreview(matter: Matter): Promise<WatermarkConfig | null> {
  try {
   // console.log('🔄 开始获取水印配置...');
    
    // 直接调用 fetchWatermarkConfigFromServer 获取配置
    const config = await this.fetchWatermarkConfigFromServer();
    
    console.log('📋 原始水印配置:', config);
    
    // 检查水印是否启用
    if (!config || !config.enabled) {
      console.log('❌ 水印未启用或配置为空');
      return null;
    }

    console.log('✅ 水印已启用，开始处理配置...');

    // 处理水印文本
    let watermarkText = config.text || '预览文件';
    
    // 添加用户信息
    const user = Moon.getSingleton().user;
    if (config.showUser && user) {
      watermarkText += ` - ${user.username}`;
    }
    
    // 添加时间信息
    if (config.showTime) {
      const now = new Date();
      watermarkText += ` - ${now.toLocaleString()}`;
    }

    // 根据文件类型调整配置
    const extension = matter.name.split('.').pop()?.toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension || '');

    let finalConfig: WatermarkConfig = {
      text: watermarkText,
      fontSize: config.fontSize || 36,
      color: config.color || 'rgba(128, 128, 128, 0.3)',
      opacity: config.opacity || 0.3,
      rotation: config.rotate || -30,
      position: 'center'
    };

    // 图片文件特殊处理
    if (isImage) {
      if (config.imageOpacity !== undefined) {
        finalConfig.opacity = config.imageOpacity;
      }
      if (config.imageText) {
        finalConfig.text = config.imageText;
      }
    }

    return finalConfig;

  } catch (error) {

    return null;
  }
}

  show(urls: string[], index: number = 0, watermarks: WatermarkConfig[] = []) {
    const images: ImageInfo[] = urls.map((url, i) => ({
      src: url,
      key: url,
      watermark: watermarks[i] || watermarks[0] // 使用对应的水印或第一个水印
    }));

    this.setState({
      visible: true,
      images,
      index
    });
  }

  render() {
    const { visible, images, index } = this.state;

    // 工具栏
    const toolBar: (overlayRenderProps: OverlayRenderProps) => React.ReactNode = (
      overlayRenderProps: OverlayRenderProps
    ) => {
      return (
        <Space>
          <RotateRightOutlined
            className="f18 toolbar-icon btn-action"
            onClick={() => {
              overlayRenderProps.onRotate(overlayRenderProps.rotate + 90);
            }}
          />
          <CloseOutlined
            className="f18 toolbar-icon btn-action"
            onClick={() => {
              this.setState({ visible: false });
              overlayRenderProps.onClose();
            }}
          />
        </Space>
      );
    };

    return (
      <div onContextMenu={(e) => e.preventDefault()}>
        <PhotoSlider
          className="widget-image-previewer"
          images={images.map(img => ({ src: img.src, key: img.key }))}
          visible={visible}
          onClose={() => {
            this.setState({ visible: false });
          }}
          index={index}
          onIndexChange={(newIndex: number) => {
            this.setState({ index: newIndex });
          }}
          toolbarRender={toolBar}
          overlayRender={(overlayProps) => {
            const currentImage = images[index];
            if (!currentImage?.watermark) return null;

            const watermark = currentImage.watermark;
            return (
              <div className="watermark-overlay">
                <div
                  className="watermark-text"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `translate(-50%, -50%) rotate(${watermark.rotation || -45}deg)`,
                    fontSize: `${watermark.fontSize || 18}px`,
                    color: watermark.color || 'rgba(255, 255, 255, 0.7)',
                    opacity: watermark.opacity || 0.7,
                    pointerEvents: 'none',
                    zIndex: 1000,
                    fontWeight: 'bold',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {watermark.text}
                </div>
              </div>
            );
          }}
        />
      </div>
    );
  }
}